import { showLevelOfCareLink } from '@/domain/levelsOfCare/useCases/showLevelOfCareLink'
import { LEVELS_OF_CARE_SECTIONS } from '@/domain/levelsOfCare/constants'
import { CareOption } from '@/components/organisms/TaxonomySections/CareOptions'
import { PrimaryFocus } from '@/domain/primaryFocus/constant'

interface CareOptionItem {
  level: string
  description?: string
}

interface CareOptionLevels {
  levels: CareOptionItem[]
}

export const getLevelsOfCareSections = (
  careOptions: CareOptionLevels,
  centerPrimaryFocus: string,
  location: string,
  treatmentServices: string[],
  whatWeTreat: string[]
): CareOption[] => {
  return careOptions?.levels
    ?.filter((item) => shouldIncludeLevel(item, treatmentServices, whatWeTreat))
    .map((item) =>
      createCareOption(
        item,
        centerPrimaryFocus,
        location,
        treatmentServices,
        whatWeTreat
      )
    )
    .filter(Boolean) as CareOption[]
}

const formatLocationSlug = (location: string): string => {
  return location?.toLowerCase()?.replace(' ', '-')
}

export const shouldIncludeLevel = (
  item: CareOptionItem,
  treatmentServices: string[],
  whatWeTreat: string[]
): boolean => {
  const level = LEVELS_OF_CARE_SECTIONS[item.level]
  if (
    item.level === 'Detox' &&
    treatmentServices.includes('Licensed Primary Mental Health')
  ) {
    return false
  }
  return (
    level?.required ||
    isTreatmentServiceProvided(
      item.level,
      level?.aliases || [],
      treatmentServices,
      whatWeTreat
    )
  )
}

const createCareOption = (
  item: CareOptionItem,
  centerPrimaryFocus: string,
  location: string,
  treatmentServices: string[],
  whatWeTreat: string[]
): CareOption | null => {
  const level = LEVELS_OF_CARE_SECTIONS[item.level]
  if (!level) return null

  const careOption: CareOption = {
    label:
      item.level === 'Residential' && centerPrimaryFocus === 'Substance Use'
        ? 'Residential Rehab'
        : item.level,
    description: item.description || level.description,
    isTreatmentServiceProvided: isTreatmentServiceProvided(
      item.level,
      level.aliases || [],
      treatmentServices,
      whatWeTreat
    )
  }

  if (
    showLevelOfCareLink(centerPrimaryFocus as PrimaryFocus, item.level) &&
    location
  ) {
    careOption.linkText = level.linkText
      ? level.linkText(location, centerPrimaryFocus)
      : null
    careOption.url = level.url
      ? level.url(formatLocationSlug(location), centerPrimaryFocus)
      : null
  }

  return careOption
}

export const isTreatmentServiceProvided = (
  label: string,
  aliases: string[] = [],
  treatmentServices: string[],
  whatWeTreat: string[]
): boolean => {
  return (
    treatmentServices.includes(label) ||
    aliases.some((alias) => treatmentServices.includes(alias)) ||
    (label.startsWith('Co-Occurring') &&
      whatWeTreat.includes('Co-Occurring Disorders'))
  )
}
